import React, { useEffect, useState, useCallback } from "react";
import { Button, Form, Table } from "react-bootstrap";
import { FaTrash, FaPlus, FaEdit, FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "../../../assets/css/Variant/VariantValueList.css";

const API_BASE_URL = process.env.REACT_APP_API_URL;

const VariantValueList = () => {
  const [variantValues, setVariantValues] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const userPermissions = JSON.parse(localStorage.getItem("permissions") || "[]");
  const canRead = userPermissions.includes("read_VariantValue");
  const canCreate = userPermissions.includes("create_VariantValue");
  const canEdit = userPermissions.includes("update_VariantValue");
  const canDelete = userPermissions.includes("delete_VariantValue");
  const canToggleStatus = userPermissions.includes("update_VariantValue");

  const fetchVariantValues = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/variant-values`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVariantValues(res.data);
    } catch (error) {
      console.error("Error fetching variant values:", error);
    }
  }, [token]);

  useEffect(() => {
    fetchVariantValues();
  }, [fetchVariantValues]);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this?")) {
      try {
        await axios.delete(`${API_BASE_URL}/api/variant-values/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        fetchVariantValues();
      } catch (error) {
        console.error("Error deleting variant value:", error);
      }
    }
  };

  const handleStatusToggle = async (id) => {
    try {
      await axios.put(
        `${API_BASE_URL}/api/variant-values/toggle-status/${id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchVariantValues();
    } catch (error) {
      console.error("Error toggling status:", error);
    }
  };

  const filteredData = variantValues.filter((item) => {
    const variantNameMatch = item?.variantName?.name?.toLowerCase().includes(search.toLowerCase());
    const valuesMatch = item?.values?.some((val) =>
      val?.toLowerCase().includes(search.toLowerCase())
    );
    return variantNameMatch || valuesMatch;
  });

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentData = filteredData.slice(startIndex, startIndex + itemsPerPage);

  const handlePrev = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  };

  const handleNext = () => {
    if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
  };

  return (
    <div className="container py-4 variant-value-container">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="title-color">Variant Value</h4>
        <Button
          className="btn-custom"
          onClick={() => navigate("/add-variant-value")}
          disabled={!canCreate}
          title={canCreate ? "Add Variant Value" : "You don't have permission to add"}
        >
          <FaPlus className="me-1" /> Add Variant Value
        </Button>
      </div>

      <Form.Control
        type="text"
        placeholder="🔍 Search by Variant Name or Values..."
        value={search}
        className="mb-3 search-input"
        onChange={(e) => {
          setSearch(e.target.value);
          setCurrentPage(1);
        }}
      />

      {canRead ? (
        <>
          <Table striped bordered hover responsive className="variant-table">
            <thead className="table-header">
              <tr>
                <th style={{ width: "5%" }}>No.</th>
                <th style={{ width: "25%" }}>Variant Name</th>
                <th style={{ width: "25%" }}>Values</th>
                <th style={{ width: "25%" }}>Remarks</th>
                <th style={{ width: "10%" }}>Status</th>
                <th style={{ width: "10%" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {currentData.length > 0 ? (
                currentData.map((item, index) => (
                  <tr key={item._id}>
                    <td>{startIndex + index + 1}</td>
                    <td>{item?.variantName?.name || "N/A"}</td>
                    <td>
                      {item?.values && item.values.length > 0
                        ? item.values.join(", ")
                        : "-"}
                    </td>
                    <td>{item?.remarks || "-"}</td>
                    <td>
                      <Form.Check
                        type="switch"
                        id={`status-switch-${item._id}`}
                        checked={item.status}
                        onChange={() => canToggleStatus && handleStatusToggle(item._id)}
                        disabled={!canToggleStatus}
                        className="status-switch1"
                      />
                    </td>
                    <td>
                      <div className="d-flex gap-2 justify-content-center">
                        <Button
                          variant="info"
                          size="sm"
                          className="btn-edit"
                          onClick={() => navigate(`/edit-variant-value/${item._id}`)}
                          title={canEdit ? "Edit" : "No edit permission"}
                          disabled={!canEdit}
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          className="btn-delete"
                          onClick={() => canDelete && handleDelete(item._id)}
                          title={canDelete ? "Delete" : "No delete permission"}
                          disabled={!canDelete}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center text-muted">
                    No variant values found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          {totalPages > 1 && (
            <div className="d-flex justify-content-end align-items-center gap-3 mt-3">
              <Button
                variant="secondary"
                onClick={handlePrev}
                disabled={currentPage === 1}
              >
                <FaArrowLeft className="me-1" /> Previous
              </Button>

              <span>
                Page {currentPage} of {totalPages}
              </span>

              <Button
                variant="secondary"
                onClick={handleNext}
                disabled={currentPage === totalPages}
              >
                Next <FaArrowRight className="ms-1" />
              </Button>
            </div>
          )}
        </>
      ) : (
        <div className="text-center text-danger">
          You do not have permission to view Variant Values.
        </div>
      )}
    </div>
  );
};

export default VariantValueList;
